import { getBridge } from '@packages/shared/src/utils/getBridge';
import type { Promotion } from '@packages/cms-components/src/modules/PromotionBanner/types';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import {
  Accordion,
  AccordionDetails,
  AccordionItem,
  AccordionSummary,
} from '@packages/shared/src/ui/Accordion/Accordion';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Card } from '@packages/shared/src/ui/Card/Card';
import { logger } from '@packages/shared/src/utils/logger/logger';
import { useSetAtom } from 'jotai';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { remotePromotion } from '../../atoms';
import { classes } from '../../utils/classes';
import type { VoucherItemType } from './types';
import { messages } from './Voucher.messages';

const fetchAndActivatePromotion = async (
  promotionCode: string,
  setVoucher: (promotionData: Promotion) => void,
) => {
  fetch(`/api/promotions/promotion/${promotionCode}`)
    .then((response) => {
      if (response.ok) {
        return response.json();
      }
      throw new Error(response.statusText);
    })
    .then((promotionData: Promotion) => {
      if (promotionData) {
        setVoucher(promotionData);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        throw new Error(`no promotion data for promotionCode ${promotionCode}`);
      }
    })
    .catch((error) => {
      logger.error(error, 'set remote promotion error');
    });
};

const ActivateVoucherButton = ({
  promotionCode,
  fullWidth = false,
}: {
  promotionCode?: string | null;
  fullWidth?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const setVoucher = useSetAtom(remotePromotion);
  const [disabled, setDisabled] = useState(false);
  return (
    <Button
      onClick={async () => {
        if (promotionCode) {
          setDisabled(true);
          // INSPIRE-3533: set bonuscodes also for usage by native apps
          getBridge().setBonuscodes(promotionCode);
          // we could use fetchPromoData() here but this action and fetch is done clientside -
          // we would get a CORS-Error -> so instead we fetch our promotion data by our api
          fetchAndActivatePromotion(promotionCode, setVoucher).then(() => setDisabled(false));
        }
      }}
      size="medium"
      className={fullWidth ? 'w-full' : 'w-fit'}
      disabled={disabled}
    >
      {formatMessage(messages.voucherActivate)}
    </Button>
  );
};

export const VoucherItem = ({
  voucherData: {
    voucherValue,
    badgeSubline,
    seoHeadline,
    promotionCode,
    seoDescription,
    detailInfos,
  },
  expired,
}: {
  voucherData: VoucherItemType;
  expired: boolean;
}) => {
  const { formatMessage } = useIntl();
  const { isMobile } = useDeviceType();
  return (
    <Card
      className={classes(
        'w-sm md:w-3xl',
        expired ? 'text-color-text-darktransparent' : 'text-color-text-primary',
      )}
    >
      <div className="flex w-full flex-row flex-wrap items-center p-2">
        <div
          className={classes(
            'flex basis-1/3 flex-col text-center md:basis-1/6',
            expired ? 'text-color-text-darktransparent' : 'text-color-primary',
          )}
        >
          <div className="text-size-headline-3xl">{voucherValue}</div>
          <div className="text-size-body-sm break-words">{badgeSubline}</div>
        </div>
        <div className="flex basis-2/3 flex-col md:basis-5/6">
          <div className="text-size-headline-xl mb-2 break-words">{seoHeadline}</div>
          {!expired && !isMobile && <ActivateVoucherButton promotionCode={promotionCode} />}
        </div>
      </div>
      {!expired && isMobile && (
        <div className="flex basis-full flex-col px-2 pb-2">
          <ActivateVoucherButton promotionCode={promotionCode} fullWidth />
        </div>
      )}
      <Accordion
        className={expired ? 'text-color-text-darktransparent' : 'text-color-text-primary'}
        type="single"
        collapsible
      >
        <AccordionItem value="item-1" className="m-0! flex flex-col-reverse shadow-none">
          <AccordionSummary
            icon="icon-emp-arrow-medium-down"
            className="border-t-color-separator [&[data-state=open]]:border-t-1"
          >
            {formatMessage(messages.voucherMoreInformation)}
          </AccordionSummary>
          <AccordionDetails className="text-size-body-sm p-2">
            {seoDescription}
            <div className="mt-2.5 flex flex-wrap">
              {detailInfos?.map(({ key, value }, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className="flex basis-full lg:basis-1/2" key={index}>
                  <span className="text-size-body-sm mr-0.5 font-bold">{key}</span>
                  {value}
                </div>
              ))}
            </div>
          </AccordionDetails>
        </AccordionItem>
      </Accordion>
    </Card>
  );
};
